"use client";

import React, { useState } from "react";
import SubtleButton from "@/src/shared/components/atoms/SubtleButton";
import { formatReviewAverage } from "@/src/shared/utils/formatReviewAverage";
import { usePublicListingReviewsQuery } from "../api/queries";
import type { IReviewBadge } from "../types";
import AllReviewsDialog from "./AllReviewsDialog";
import MentionChips from "./MentionChips";
import { cn } from "@/src/lib/utils";
import { listingBadgeService } from "@/src/shared/services/listingBadge";
import PublicReviewCard from "./PublicReviewCard";

/** Cards shown on the page before "Show all N reviews": the app's 6 sideways on mobile. */
const CARDS_ON_PAGE = 6;
/** Desktop shows two rows of two, and needs no "Show all" when that is every review. */
const CARDS_ON_DESKTOP = 4;
const GUEST_FAVOURITE_TEXT =
  "One of the most loved stays on Travela, based on ratings, reviews and reliability.";

interface IListingReviewsProps {
  listingId: number;
  /** The listing's own numbers, shown until the reviews API answers. */
  reviewsAvg: number | null | undefined;
  reviewsCount: number | null | undefined;
  /** The listing's badges; the API's summary carries the same rows. */
  badges?: IReviewBadge[];
}

const thousands = (n: number) => n.toLocaleString("en-US");

/**
 * The listing page's reviews, from the app's reviews API: the Guest Favourite banner or
 * the score line, the "guests mention" chips, the latest reviews, and every review in a
 * dialog. Sideways cards on mobile (as the app), a two-column grid on desktop.
 */
const ListingReviews: React.FC<IListingReviewsProps> = ({
  listingId,
  reviewsAvg,
  reviewsCount,
  badges = [],
}) => {
  const { data, isError } = usePublicListingReviewsQuery(listingId);
  const [dialog, setDialog] = useState<{ mention: string | null } | null>(null);

  const summary = data?.pages[0]?.summary ?? null;
  const reviews = data?.pages[0]?.data ?? [];
  const count = summary?.reviews_count ?? reviewsCount ?? 0;
  const average = summary?.average ?? reviewsAvg ?? 0;
  const mentions = summary?.mentions ?? [];
  const favourite = [...(summary?.badges ?? []), ...badges].find((badge) =>
    listingBadgeService.isHighlyRecommended(badge.slug),
  );

  // As the app: nothing to show when the API failed and there is no badge to show.
  if (isError && !favourite) return null;

  return (
    <section id="reviews" className="scroll-mt-24">
      {favourite ? (
        <div className="flex flex-col items-center px-4 py-6 text-center">
          <div className="flex items-center gap-1">
            <img
              src="/icons/listing/laurelleft.svg"
              alt=""
              className="h-[82px] w-[55px]"
            />
            <span className="text-54 font-extrabold leading-[44px] tracking-[-0.54px] text-ink">
              {formatReviewAverage(average)}
            </span>
            <img
              src="/icons/listing/laurelright.svg"
              alt=""
              className="h-[82px] w-[55px]"
            />
          </div>
          <h2 className="mt-4 text-22 font-bold text-ink">
            {favourite.name || "Guest Favorite"}
          </h2>
          <p className="mt-2 max-w-[276px] text-xs font-medium text-ink-secondary">
            {favourite.description || GUEST_FAVOURITE_TEXT}
          </p>
        </div>
      ) : count === 0 ? (
        <div className="py-6">
          <h2 className="flex items-center gap-2 text-22 font-semibold tracking-[-0.2px] text-ink">
            <img
              src="/icons/listing/star_listing_details.svg"
              alt=""
              className="h-3 w-3"
            />
            New <span aria-hidden>·</span> No reviews yet
          </h2>
          <p className="ml-5 mt-2 text-base font-medium text-ink-secondary">
            Be the first to review this place.
          </p>
        </div>
      ) : (
        <h2 className="flex items-center gap-2 py-6 text-22 font-semibold tracking-[-0.2px] text-ink">
          <img
            src="/icons/listing/star_listing_details.svg"
            alt=""
            className="h-3 w-3"
          />
          {formatReviewAverage(average)} <span aria-hidden>·</span>{" "}
          {thousands(count)} {count === 1 ? "review" : "reviews"}
        </h2>
      )}

      {count > 0 && (
        <>
          {mentions.length > 0 && (
            <MentionChips
              mentions={mentions}
              onSelect={(slug) => setDialog({ mention: slug })}
            />
          )}

          <div className="-mx-4 mt-6 flex snap-x scroll-px-4 overflow-x-auto px-4 scrollbar-hide md:mx-0 md:mt-8 md:grid md:grid-cols-2 md:gap-x-24 md:gap-y-10 md:overflow-visible md:px-0">
            {reviews.slice(0, CARDS_ON_PAGE).map((review, index) => (
              <div
                key={review.id}
                className={cn(
                  index === 0
                    ? "w-[264px] shrink-0 snap-start pr-5 md:w-auto md:pr-0"
                    : "w-[264px] shrink-0 snap-start border-l border-[#EBEBEB] px-5 md:w-auto md:border-l-0 md:px-0",
                  index >= CARDS_ON_DESKTOP && "md:hidden",
                )}
              >
                <PublicReviewCard
                  review={review}
                  clamp
                  onShowMore={() => setDialog({ mention: null })}
                />
              </div>
            ))}
          </div>

          {/* The app's grey button on mobile; Airbnb's lighter, squarer one on desktop. */}
          <SubtleButton
            className={cn(
              "mt-6 md:mt-10 md:h-12 md:rounded-lg md:bg-[#F2F2F2] md:hover:bg-[#EBEBEB]",
              count <= CARDS_ON_DESKTOP && "md:hidden",
            )}
            onClick={() => setDialog({ mention: null })}
          >
            Show all {thousands(count)} {count === 1 ? "review" : "reviews"}
          </SubtleButton>
        </>
      )}

      <AllReviewsDialog
        listingId={listingId}
        isOpen={dialog !== null}
        onClose={() => setDialog(null)}
        title={`${thousands(count)} ${count === 1 ? "review" : "reviews"}`}
        mentions={mentions}
        mention={dialog?.mention ?? null}
        onMentionChange={(mention) => setDialog({ mention })}
      />
    </section>
  );
};

export default ListingReviews;
